<template>
  <div class="publisher-deal-tiles row">
    <div
      id="filters-container"
      class="col-12">
      <div class="table-container filters">
        <Table
          v-if="isReady"
          id="packages_list_table"
          ref="table"
          category="Packages"
          :items="packages"
          :columns="columns"
          :table-download-columns="tableDownloadColumns"
          :expand="[
            'package_properties',
            'package_external_exchange_restrictions',
            ...(isCuratorOrganization
              ? [
                  'inventory_targeting.co2e_emissions.score',
                  'package_children.inventory_targeting.co2e_emissions.score',
                  'package_fee',
                ]
              : []),
          ]"
          :show-filters="true"
          :fixed-filters="fixedFilters"
          :total-count="totalCount"
          :text-search="tableTextSearch"
          :sort-column="sortBy"
          :sort-ascending="isSortAscending"
          :is-loading="isLoading"
          :text-search-properties="['package_id', 'deal_id', 'name', 'package_properties.value']"
          :is-main-table="false"
          :show-table="false"
          :enable-download="true"
          :reset-selection-on-update="false"
          :lock-selection="lockSelection"
          :can-select-item="isItemSelectable"
          pagination
          allow-selection
          @page-size-changed="(val) => $emit('pageSizeChanged', val)"
          @download="(val) => $emit('onDownload', val)"
          @filters-changed="(val) => tableFiltersChangedHandler(val)"
          @selection-changed="(val) => $emit('selection-changed', val)">
          <template #extra-controls>
            <tiles-sort-control
              :sort-direction="sortDirection"
              :sort-by="sortBy"
              :sort-direction-options="sortDirectionOptions"
              :sort-by-options="sortByOptions"
              :enable-download="true"
              @sort-direction-changed="(x) => $emit('sortDirectionChanged', x)"
              @sort-by-changed="(x) => $emit('sortByChanged', x)"
              @download-clicked="() => $emit('tileDownloadClicked')" />
          </template>
        </Table>
      </div>
    </div>
  </div>
  <div
    v-if="isLoading"
    class="loader-container">
    <loader-small class="mr-3" />
    {{ capitalize($t('states.loading')) }}
  </div>
  <div v-else>
    <div class="row">
      <div class="col-12">
        <div
          v-if="isCuratorOrganization"
          class="curated-deal-tiles-grid">
          <curated-deal-tile
            v-for="pack in packages"
            :key="pack.id"
            :deal="pack"
            :demographics="demographics"
            :is-loading="isLoading"
            :locations="locations"
            :markets="markets"
            :media-types="mediaTypes"
            :suppliers="mediaOwners"
            :buyer-organization-d-s-ps="buyerOrganizationDSPs" />
        </div>
        <div
          v-else
          class="deal-tiles-grid">
          <package-tile
            v-for="pack in packages"
            :key="pack.id"
            :deal="pack"
            :all-demographics="demographics"
            :all-media-owners="mediaOwners"
            :buyer-organization-d-s-ps="buyerOrganizationDSPs"
            :is-selected="isItemSelected(pack, selectedPackages)"
            :can-select-item="!lockSelection && isItemSelectable(pack, selectedPackages)"
            @selection-changed="table.handleItemSelection(pack, !isItemSelected(pack, selectedPackages))" />
        </div>
      </div>
      <table
        v-show="!isLoading && packages.length === 0 && isReady"
        class="table">
        <tbody>
          <tr>
            <td>
              <no-records-table
                :icon="markRaw(IconComplexDeal2)"
                :header-text="i18n.t('messages.tablePlaceholderNoDealsTitleDemandSize')"
                :details-text="i18n.t('messages.tablePlaceholderNoDealsDescriptionDemandSize')" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <table-footer
      v-show="totalCount > 10"
      :total-count="totalCount"
      :page-index="pageIndex"
      :page-size="pageSize"
      @page-index-changed="(value) => (pageIndex = value)"
      @page-size-changed="(value) => emits('pageSizeChanged', value)" />
  </div>
</template>

<script setup lang="ts">
  import { capitalize, markRaw, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import LoaderSmall from '@/common/components/controls/LoaderSmall.vue';
  import NoRecordsTable from '@/common/components/controls/table/NoRecordsTable.vue';
  import Table from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import CuratedDealTile from '@/common/components/deals/CuratedDealTile.vue';
  import { isItemSelectable, isItemSelected } from '@/common/components/deals/DealsHelper';
  import PackageTile from '@/common/components/deals/PackageTile.vue';
  import { IconComplexDeal2 } from '@/common/icons/complex';
  import { Demographic, Market, MediaType, OrganizationDSP, Package, QueryParams } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import Filter from '@/common/models/query-params/Filter';
  import TableFooter from '@/org-app/components/controls/TableFooter.vue';
  import { ISortBy, ISortDirection } from '@/org-app/components/packages/DealSorting';
  import TilesSortControl from '@/org-app/components/packages/TilesSortControl.vue';

  interface IDealsTilesTableProps {
    packages: Package[];
    mediaOwners: MediaOwner[];
    buyerOrganizationDSPs: OrganizationDSP[];
    fixedFilters: Filter[];
    demographics: Demographic[];
    mediaTypes: MediaType[];
    locations?: Location[];
    markets?: Market[];
    columns: Record<string, TableColumn>;
    tableDownloadColumns: Record<string, TableColumn>;
    sortBy?: ISortBy;
    sortDirection?: ISortDirection;
    sortDirectionOptions?: ISortDirection[];
    sortByOptions?: ISortBy[];
    totalCount: number;
    pageSize: number;
    tableTextSearch: string;
    isReady: boolean;
    isLoading: boolean;
    isSortAscending: boolean;
    isCuratorOrganization: boolean;
    previousTableData: typeof Table | null;
    selectedPackages?: Package[];
    lockSelection?: boolean;
  }

  const props = withDefaults(defineProps<IDealsTilesTableProps>(), {
    packages: () => [],
    mediaOwners: () => [],
    buyerOrganizationDSPs: () => [],
    fixedFilters: null,
    demographics: () => [],
    mediaTypes: () => [],
    locations: () => [],
    markets: () => [],
    columns: null,
    tableDownloadColumns: null,
    sortBy: null,
    sortDirection: null,
    sortDirectionOptions: null,
    sortByOptions: null,
    totalCount: 0,
    pageSize: 10,
    tableTextSearch: '',
    isReady: false,
    isLoading: true,
    isSortAscending: false,
    isCuratorOrganization: false,
    previousTableData: null,
    selectedPackages: () => [],
    lockSelection: false,
  });

  const emits = defineEmits([
    'pageSizeChanged',
    'onDownload',
    'filtersChanged',
    'tileDownloadClicked',
    'sortDirectionChanged',
    'sortByChanged',
    'onTableLoaded',
    'selection-changed',
  ]);

  const i18n = useI18n();
  const table = ref(null);
  const pageIndex = ref(props.previousTableData?.pageIndex ?? 1);
  const restoredData = ref(props.previousTableData);
  const shouldUpdatePage = ref(true);

  const tableFiltersChangedHandler = (x: QueryParams) => {
    if (restoredData.value) {
      restoredData.value = null;
      return;
    }
    if (table.value.pageIndex !== pageIndex.value) {
      shouldUpdatePage.value = false;
      pageIndex.value = table.value.pageIndex;
      shouldUpdatePage.value = true;
    }
    emits('filtersChanged', x);
  };

  watch(
    () => pageIndex.value,
    () => {
      if (shouldUpdatePage.value) table.value?.setPageIndex(pageIndex.value);
    },
  );

  watch(
    () => props.pageSize,
    () => {
      table.value?.setPageSize(props.pageSize);
    },
  );

  watch(
    () => table.value,
    () => {
      if (table.value) emits('onTableLoaded', table.value);
    },
  );
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 4.8rem;
    font-size: 1.2em;
  }

  .page-size-container {
    display: flex;
    flex-direction: row-reverse;

    .multiselect {
      flex-basis: auto;
      max-width: 19rem;
    }
  }

  #filters-container {
    margin-bottom: 2rem;
  }

  #app {
    .packages-details-container {
      .filters {
        display: flex;
        flex-direction: row;
        width: 100%;
        padding-top: 0.8rem;
        vertical-align: center;

        section {
          width: 100%;
        }

        > * {
          margin-right: 1rem;
          width: 100%;
        }

        .btn {
          margin-left: 0.8rem;
        }

        .input-container.daterangepicker-input {
          width: 21rem !important;
          margin-right: 0.8rem;
        }
      }
    }
  }

  @media screen and (max-width: 1200px) {
    #app {
      .filters {
        flex-direction: column;

        > div {
          margin-bottom: 1rem;
        }
      }
    }
  }

  .deal-tiles-grid {
    display: grid;
    column-gap: 2.4rem;
    grid-template-columns: repeat(auto-fill, minmax(33rem, 1fr));
  }

  .curated-deal-tiles-grid {
    margin-top: 1.6rem;
    margin-bottom: 3.2rem;
    display: grid;
    grid-gap: 3rem;
    grid-template-columns: repeat(auto-fit, minmax(40rem, 1fr));
  }
</style>
